import * as feedSync from './feedSync';
import eventEmitter, { EVENTS } from './eventService';
import { PollingConfiguration, PollingConfigManager, getPollingConfig } from './pollingConfig';
import logger, { LogCategory } from '../utils/logger';

class PollingService {
  private userHazardsTimer: NodeJS.Timeout | null = null;
  private communityHazardsTimer: NodeJS.Timeout | null = null;
  private isPolling = false;
  private retryCount = 0;
  private onlineListener: () => void;
  private offlineListener: () => void;
  /** When true, WebSocket is healthy — hazard polls are suppressed (HTTP fallback only when realtime is down). */
  private realtimePrimary = false;

  private config: PollingConfiguration = PollingConfigManager.loadConfig();

  constructor() {
    // Set up online/offline event listeners
    this.onlineListener = () => {
      console.log('🌐 PollingService: Connection restored - resuming polling');
      
      // Note: Sync is now handled by App.tsx global handlers
      // Just resume polling if it was running before going offline
      if (this.isPolling) {
        this.restart();
      }
    };
    
    this.offlineListener = () => {
      console.log('📴 Connection lost - pausing polling');
      // Keep isPolling flag but stop actual timers
      this.pauseTimers();
    };

    // Add event listeners
    window.addEventListener('online', this.onlineListener);
    window.addEventListener('offline', this.offlineListener);
  }

  /**
   * Initialize polling service when user is authenticated
   */
  public initialize(): void {
    if (this.isPolling) {
      console.log('🔄 Polling service already running');
      return;
    }

    // Load current configuration
    this.config = getPollingConfig();
    
    // Check if polling should be enabled based on configuration
    if (!this.config.enabled) {
      console.log('📵 Polling disabled by configuration');
      return;
    }

    console.log('🚀 Starting hazard data polling service...', {
      userInterval: this.config.userHazardsInterval,
      communityInterval: this.config.communityHazardsInterval,
      isOnline: navigator.onLine
    });
    
    this.isPolling = true;
    this.retryCount = 0;
    
    // Start polling immediately with a small delay to let app finish loading
    setTimeout(() => {
      this.startUserHazardsPolling();
      this.startCommunityHazardsPolling();
    }, 2000); // 2 second delay after app load
  }

  /**
   * Stop all polling activities
   */
  public stop(): void {
    console.log('🛑 Stopping hazard data polling service...');
    this.isPolling = false;
    this.realtimePrimary = false;
    this.pauseTimers();
  }

  /**
   * Pause timers without changing polling state
   */
  private pauseTimers(): void {
    if (this.userHazardsTimer) {
      clearTimeout(this.userHazardsTimer);
      this.userHazardsTimer = null;
    }
    
    if (this.communityHazardsTimer) {
      clearTimeout(this.communityHazardsTimer);
      this.communityHazardsTimer = null;
    }
  }

  /**
   * Restart polling (useful for config changes or reconnection)
   */
  public restart(): void {
    if (this.isPolling) {
      this.pauseTimers();
      this.config = getPollingConfig();
      
      if (this.config.enabled && navigator.onLine) {
        setTimeout(() => {
          this.startUserHazardsPolling();
          this.startCommunityHazardsPolling();
        }, 1000);
      }
    }
  }

  /**
   * Update polling configuration
   */
  public updateConfig(newConfig: Partial<PollingConfiguration>): void {
    const updatedConfig = { ...this.config, ...newConfig };
    PollingConfigManager.saveConfig(updatedConfig);
    this.config = updatedConfig;
    
    // Restart polling with new config if currently running
    if (this.isPolling) {
      this.restart();
    }
  }

  /**
   * Apply a configuration preset
   */
  public applyPreset(preset: 'HIGH_FREQUENCY' | 'NORMAL' | 'LOW_FREQUENCY' | 'BATTERY_SAVER'): void {
    this.config = PollingConfigManager.applyPreset(preset);
    
    if (this.isPolling) {
      this.restart();
    }
  }

  /**
   * Check if polling is currently active
   */
  public isActive(): boolean {
    return this.isPolling;
  }

  /**
   * WebSocket health is tracked for callers. The feed window still refreshes
   * on the prefix (60s) and full (5 min) cadence.
   */
  public setRealtimePrimary(isPrimary: boolean): void {
    if (this.realtimePrimary === isPrimary) {
      return;
    }
    this.realtimePrimary = isPrimary;
  }

  /**
   * Get current polling configuration
   */
  public getConfig(): PollingConfiguration {
    return { ...this.config };
  }

  /**
   * Start polling for user's own hazards
   */
  private startUserHazardsPolling(): void {
    if (!this.config.enabled || !this.isPolling) return;

    const pollUserHazards = async () => {
      try {
        if (!navigator.onLine) {
          console.log('📴 Offline - skipping user hazards poll');
          this.scheduleNextUserHazardsPoll();
          return;
        }

        logger.debug(LogCategory.HAZARDS, 'Polling full community window...');
        await feedSync.request('full');
        eventEmitter.emit(EVENTS.HAZARDS_UPDATED);
        
        // Reset retry count on success
        this.retryCount = 0;
        this.scheduleNextUserHazardsPoll();
        
      } catch (error) {
        console.error('❌ Error polling user hazards:', error);
        this.handlePollingError('user');
      }
    };

    // Start first poll
    pollUserHazards();
  }

  /**
   * Start polling for community hazards  
   */
  private startCommunityHazardsPolling(): void {
    if (!this.config.enabled || !this.isPolling) return;

    const pollCommunityHazards = async () => {
      try {
        if (!navigator.onLine) {
          console.log('📴 Offline - skipping community hazards poll');
          this.scheduleNextCommunityHazardsPoll();
          return;
        }

        console.log('🔄 Polling community hazards for updates...');
        await feedSync.request('prefix');
        
        console.log('✅ Community hazards polling completed');
        // Emit event to notify UI of potential updates
        eventEmitter.emit(EVENTS.COMMUNITY_HAZARDS_UPDATED);
        
        // Reset retry count on success
        this.retryCount = 0;
        this.scheduleNextCommunityHazardsPoll();
        
      } catch (error) {
        console.error('❌ Error polling community hazards:', error);
        this.handlePollingError('community');
      }
    };

    // Start first poll with slight delay to stagger from user hazards.
    // Store the handle so pauseTimers() can cancel it when realtime is primary.
    this.communityHazardsTimer = setTimeout(pollCommunityHazards, 5000);
  }

  /**
   * Schedule next user hazards poll
   */
  private scheduleNextUserHazardsPoll(): void {
    if (!this.isPolling) return;
    
    this.userHazardsTimer = setTimeout(() => {
      this.startUserHazardsPolling();
    }, 5 * 60 * 1000);
  }

  /**
   * Schedule next community hazards poll
   */
  private scheduleNextCommunityHazardsPoll(): void {
    if (!this.isPolling) return;
    
    this.communityHazardsTimer = setTimeout(() => {
      this.startCommunityHazardsPolling();
    }, this.config.communityHazardsInterval);
  }

  /**
   * Handle polling errors with exponential backoff
   */
  private handlePollingError(type: 'user' | 'community'): void {
    this.retryCount++;
    
    if (this.retryCount >= this.config.maxRetries) {
      console.error(`❌ Max polling retries (${this.config.maxRetries}) reached for ${type} hazards. Stopping polls.`);
      // Emit error event
      eventEmitter.emit(EVENTS.POLLING_ERROR, { type, retryCount: this.retryCount });
      
      // Reset retry count and continue with normal interval
      this.retryCount = 0;
      if (type === 'user') {
        this.scheduleNextUserHazardsPoll();
      } else {
        this.scheduleNextCommunityHazardsPoll();
      }
      return;
    }

    // Calculate backoff delay: base delay * 2^(retryCount-1)
    const backoffDelay = this.config.retryBackoffMs * Math.pow(2, this.retryCount - 1);
    console.log(`⏰ Retrying ${type} hazards poll in ${backoffDelay}ms (attempt ${this.retryCount}/${this.config.maxRetries})`);
    
    // Schedule retry with backoff
    setTimeout(() => {
      if (type === 'user') {
        this.startUserHazardsPolling();
      } else {
        this.startCommunityHazardsPolling();
      }
    }, backoffDelay);
  }

  /**
   * Trigger immediate sync of all data (useful for manual refresh)
   */
  public async forceSyncAll(): Promise<void> {
    if (!navigator.onLine) {
      throw new Error('Cannot sync while offline');
    }

    console.log('🔄 Force syncing all hazard data...');
    
    try {
      await feedSync.request('full');
      console.log('✅ Force synced community hazards');
      
      // Emit events to update UI
      eventEmitter.emit(EVENTS.HAZARDS_UPDATED);
      eventEmitter.emit(EVENTS.COMMUNITY_HAZARDS_UPDATED);
      
    } catch (error) {
      console.error('❌ Error during force sync:', error);
      throw error;
    }
  }

  /**
   * Get polling status information
   */
  public getStatus(): {
    isPolling: boolean;
    retryCount: number;
    config: PollingConfiguration;
    isOnline: boolean;
    timersActive: boolean;
  } {
    return {
      isPolling: this.isPolling,
      retryCount: this.retryCount,
      config: this.getConfig(),
      isOnline: navigator.onLine,
      timersActive: this.userHazardsTimer !== null || this.communityHazardsTimer !== null
    };
  }

  /**
   * Cleanup method to remove event listeners
   */
  public cleanup(): void {
    this.stop();
    window.removeEventListener('online', this.onlineListener);
    window.removeEventListener('offline', this.offlineListener);
  }
}

// Create and export singleton instance
const pollingService = new PollingService();
export default pollingService;